/**
 * Match clock utilities.
 *
 * Clock state lives in matchMeta (persisted in DB) so that:
 *  - page reloads restore the correct minute
 *  - multiple editors see the same minute
 *
 * Formula for current minute:
 *   minute = clockOffset + floor((now - clockStartedAt - totalPauseMs) / 60000)
 * If paused:
 *   minute = clockOffset + floor((pausedAt - clockStartedAt - totalPauseMs) / 60000)
 */

/**
 * Calculate the current display minute from matchMeta clock fields.
 * Returns an integer minute value.
 */
export function calculateMinute(meta) {
    if (!meta) return 0;
    if (meta.status === 'finished') return 0;
    if (!meta.clockRunning || !meta.clockStartedAt) return meta.clockOffset || 0;

    const started = new Date(meta.clockStartedAt).getTime();
    const pause = meta.totalPauseMs || 0;

    if (meta.clockPaused && meta.pausedAt) {
        const pausedAt = new Date(meta.pausedAt).getTime();
        return (meta.clockOffset || 0) + Math.floor((pausedAt - started - pause) / 60000);
    }

    return (meta.clockOffset || 0) + Math.floor((Date.now() - started - pause) / 60000);
}

/**
 * Build the matchMeta update payload for each clock action.
 * Returns an object to PATCH to /api/match-meta/:gameId
 */
export function clockAction(action, meta) {
    const now = new Date().toISOString();

    switch (action) {
        case 'kickoff':
            return {
                clockStartedAt: now,
                clockOffset: 0,
                clockRunning: true,
                clockPaused: false,
                pausedAt: null,
                totalPauseMs: 0,
                currentHalf: 1,
                status: 'live',
            };

        case 'pause':
            if (!meta.clockRunning || meta.clockPaused) return null;
            return {
                clockPaused: true,
                pausedAt: now,
            };

        case 'resume':
            if (!meta.clockPaused || !meta.pausedAt) return null;
            return {
                clockPaused: false,
                pausedAt: null,
                totalPauseMs: (meta.totalPauseMs || 0) + (Date.now() - new Date(meta.pausedAt).getTime()),
            };

        case 'halftime': {
            const htScore = meta.scoreFT || { home: 0, away: 0 };
            return {
                clockRunning: false,
                clockPaused: false,
                status: 'halftime',
                currentHalf: 1,
                scoreHT: { home: htScore.home ?? 0, away: htScore.away ?? 0 },
            };
        }

        case 'second_half':
            return {
                clockStartedAt: now,
                clockOffset: 45,
                clockRunning: true,
                clockPaused: false,
                pausedAt: null,
                totalPauseMs: 0,
                currentHalf: 2,
                status: 'live',
            };

        case 'fulltime':
            return {
                clockRunning: false,
                clockPaused: false,
                status: 'finished',
            };

        default:
            return null;
    }
}

/**
 * Format minute for display: e.g. 45 → "45'" or 90+3 → "90+3'"
 * (extra time detection: beyond 45 in 1st half, beyond 90 in 2nd half)
 */
export function formatMinute(minute, half) {
    if (minute <= 0) return '';
    const cap = half === 2 ? 90 : 45;
    if (minute > cap) {
        return `${cap}+${minute - cap}'`;
    }
    return `${minute}'`;
}
